import { useNavigate, useSearchParams } from 'react-router-dom'

/**
 * Códigos de evento do Google Ads - Centralizados
 */
export const GOOGLE_ADS_EVENTS = {
  USER_REGISTRATION: 'user_signed_up',        // Registro de novo usuário
  PLAN_FREE: 'user_plan_0',                // Plano Grátis selecionado
  PLAN_ESSENTIAL: 'user_plan_1',           // Plano Essencial selecionado
  PLAN_GROWTH: 'user_plan_2',              // Plano Growth selecionado
  PLAN_MASTER: 'user_plan_3',              // Plano Master selecionado
  PLAN_BUSINESS: 'user_plan_4',            // Plano Business selecionado
  COLOR_PALETTE_SAVED: 'param_palette_selected',      // Pallet de cores salvo
  NICHE_SELECTED: 'param_niche_completed',           // Nicho selecionado
  ART_GENERATED: 'art_generated',            // Arte gerada (novo)
  PLAN_SELECTED: 'user_selected_plan',       // Plano selecionado
}

/**
 * Mapeamento de tier do plano para código de evento do Google Ads
 */
const PLAN_EVENT_MAP = {
  0: GOOGLE_ADS_EVENTS.PLAN_FREE,
  1: GOOGLE_ADS_EVENTS.PLAN_ESSENTIAL,
  2: GOOGLE_ADS_EVENTS.PLAN_GROWTH,
  3: GOOGLE_ADS_EVENTS.PLAN_MASTER,
  4: GOOGLE_ADS_EVENTS.PLAN_BUSINESS,
}

/**
 * Gera um event_id único para tracking
 */
export const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Obtém o código de evento baseado no tier do plano
 */
export const getEventCodeByTier = (tier) => {
  return PLAN_EVENT_MAP[tier] || GOOGLE_ADS_EVENTS.USER_REGISTRATION
}

/**
 * Obtém o código de evento de plano selecionado baseado no tier
 * @param {number} tier - Tier do plano (0, 1, 2, 3)
 * @returns {string} Código do evento (ex: 'user_selected_plan_0')
 */
export const getPlanSelectedEventCode = (tier) => {
  return GOOGLE_ADS_EVENTS.PLAN_SELECTED + '_' + tier
}

/**
 * Gera os parâmetros do Google Ads para a URL
 */
export const generateGoogleAdsParams = (eventCode, eventId = null) => {
  const params = new URLSearchParams()
  const hasOriginMobile =  window.location.search.includes('origin=mobile') 
  params.set('event', eventCode)
  params.set('event_id', eventId || generateEventId())
  params.set('origin', hasOriginMobile ? 'mobile' : 'web')
  return params.toString()
}

/**
 * Hook para gerenciar tracking do Google Ads
 */
export const useGoogleAdsTracking = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  /**
   * Atualiza a URL com os parâmetros do Google Ads
   * @param {string} eventCode - Código do evento (ex: '200', '201', '202')
   * @param {string} path - Caminho da rota (opcional, usa o atual se não informado)
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const updateUrlWithGoogleAds = (eventCode, path = null, preserveEventId = true) => {
    const currentEventId = searchParams.get('event_id')
    const eventId = preserveEventId && currentEventId 
      ? currentEventId 
      : generateEventId()
    
    const params = generateGoogleAdsParams(eventCode, eventId)
    const targetPath = path || window.location.pathname
    
    navigate(`${targetPath}?${params}`, { replace: true })
  }

  /**
   * Atualiza a URL com evento baseado no tier do plano
   * @param {number} tier - Tier do plano (0, 1, 2, 3)
   * @param {string} path - Caminho da rota (opcional)
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const updateUrlWithPlanEvent = (tier, path = null, preserveEventId = true) => {
    const eventCode = getEventCodeByTier(tier)
    updateUrlWithGoogleAds(eventCode, path, preserveEventId)
  }

  /**
   * Atualiza a URL com evento de pallet de cores salvo
   * @param {string} path - Caminho da rota (opcional)
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const updateUrlWithColorPaletteEvent = (path = null, preserveEventId = true) => {
    updateUrlWithGoogleAds(GOOGLE_ADS_EVENTS.COLOR_PALETTE_SAVED, path, preserveEventId)
  }

  /**
   * Atualiza a URL com evento de nicho selecionado
   * @param {string} path - Caminho da rota (opcional)
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const updateUrlWithNicheEvent = (path = null, preserveEventId = true) => {
    updateUrlWithGoogleAds(GOOGLE_ADS_EVENTS.NICHE_SELECTED, path, preserveEventId)
  }

  /**
   * Obtém os parâmetros do Google Ads como string
   * @param {string} eventCode - Código do evento
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const getGoogleAdsParams = (eventCode, preserveEventId = true) => {
    const currentEventId = searchParams.get('event_id')
    const eventId = preserveEventId && currentEventId 
      ? currentEventId 
      : generateEventId()
    
    return generateGoogleAdsParams(eventCode, eventId)
  }

  /**
   * Obtém os parâmetros do Google Ads baseado no tier do plano
   * @param {number} tier - Tier do plano
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const getPlanEventParams = (tier, preserveEventId = true) => {
    const eventCode = getEventCodeByTier(tier)
    return getGoogleAdsParams(eventCode, preserveEventId)
  }

  /**
   * Atualiza a URL com evento de arte gerada
   * @param {string} path - Caminho da rota (opcional)
   * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
   */
  const updateUrlWithArtGeneratedEvent = (path = null, preserveEventId = true) => {
    updateUrlWithGoogleAds(GOOGLE_ADS_EVENTS.ART_GENERATED, path, preserveEventId)
  }
  /**
 * Atualiza a URL com evento de plano selecionado
 * @param {number} tier - Tier do plano selecionado (0, 1, 2, 3)
 * @param {string} path - Caminho da rota (opcional)
 * @param {boolean} preserveEventId - Se deve preservar o event_id existente (padrão: true)
 */
  const updateUrlWithPlanSelectedEvent = (tier, path = null, preserveEventId = true) => {
    const eventCode = getPlanSelectedEventCode(tier)
    updateUrlWithGoogleAds(eventCode, path, preserveEventId)
  }

  return {
    updateUrlWithGoogleAds,
    updateUrlWithPlanEvent,
    updateUrlWithColorPaletteEvent,
    updateUrlWithNicheEvent,
    updateUrlWithArtGeneratedEvent,
    updateUrlWithPlanSelectedEvent,
    getGoogleAdsParams,
    getPlanEventParams,
    generateEventId,
    getEventCodeByTier,
    getPlanSelectedEventCode,
  }
}
